<template>
    <div>
        <b-modal
            :ref="modalRef"
            title-tag="h2"
            :title="title"
            :lazy="true"
            ok-title="Convert"
            @ok="convert()"
            :ok-disabled="isOkDisabled"
            no-close-on-backdrop
            no-close-on-esc
        >
            <div v-if="details && isNewActivityWindow">
                <p>
                    Activities in Treatment Board will be created for the
                    item(s) listed and selected below.
                </p>

                <b-form-group
                    v-if="isShowSelectRange"
                    label-for="quantityRange"
                    label="Quantity:"
                    label-cols="4"
                    class="pb-2"
                >
                    <select
                        name="price"
                        class="form-control"
                        v-model="selectedQuantity"
                    >
                        <option :value="0">Apply low range</option>
                        <option :value="1">Apply high range</option>
                    </select>
                </b-form-group>
                <hr />
                <b-row class="w-100 ml-0" align-v="start">
                    <div>
                        <b-form-checkbox
                            v-model="selectedAll"
                            class="align-items-start"
                            plain
                            :disabled="!_.some(templates)"
                            @change="selectAll"
                        >
                            <strong>Select / Unselect All</strong>
                        </b-form-checkbox>
                    </div>
                </b-row>
                <hr class="mb-0" />
                <item-rows
                    v-for="template in templates"
                    :key="template.id"
                    :element="template"
                    :patientId="template.patient.id"
                    :isConvertEstimate="true"
                    :templates="templates"
                    :useHighRange="Boolean(selectedQuantity)"
                    @update-selected-all="updateSelectedAll"
                    @update-new-templates="
                        newTemplates => (templates = newTemplates)
                    "
                />
            </div>
            <div v-else-if="details && !showNext">
                <p>
                    This estimate is about to be converted into an invoice. No
                    medical records will be created for items that are not
                    assigned to patients. Discounts applied to zero quantity
                    items will be reallocated to items with a quantity that is
                    greater than zero.
                    <span v-if="isWellnessPlanEnabled()">{{
                        wellnessMsg
                    }}</span>
                    <span v-if="details.action_count > 0">{{ actionMsg }}</span>
                    Service location of the selected invoice will be used for
                    calculating taxes.
                </p>

                <b-alert
                    show
                    variant="warning"
                    v-if="
                        details.valid_provider == 1 &&
                        details.provider_required == 1
                    "
                >
                    <span id="dialog-alert-message">
                        {{ providerMsg }}
                    </span>
                </b-alert>
                <b-alert show variant="warning" v-if="preventConvert">
                    <span id="dialog-alert-message">
                        {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY }}
                    </span>
                </b-alert>
                <b-alert show variant="warning" v-if="declinedExists">
                    <span id="dialog-alert-message">
                        Items that have been declined will be omitted in the
                        invoice.
                    </span>
                </b-alert>

                <hr />

                <b-form-group
                    v-if="details.range == 1 && !isShowSelectRange"
                    label-for="price"
                    label="Price:"
                    label-cols="4"
                >
                    <select
                        name="price"
                        class="form-control"
                        v-model="selectedPrice"
                        :disabled="isAwaitingInformation"
                    >
                        <option :value="0">Apply low range</option>
                        <option :value="1">Apply high range</option>
                    </select>
                </b-form-group>

                <!-- if no client, show client v-select -->

                <b-form-group
                    label-for="medical-note"
                    label="Medical Note:"
                    label-cols="4"
                >
                    <v-select
                        v-if="details.active_soap.length > 0"
                        id="medical-notes"
                        :options="noteOptions"
                        v-model="multipleNotes"
                        placeholder="Medical Note (optional)"
                        name="medical-notes"
                        label="label_in_estimate"
                        @input="onNotesChange"
                        :class="{ 'border-danger': notesHasError }"
                        multiple
                        :disabled="isAwaitingInformation"
                    />
                    <select
                        v-else
                        id="medical-notes"
                        v-model="singleNote"
                        class="form-control"
                        :disabled="isAwaitingInformation"
                    >
                        <option :value="null">
                            Do not create medical note
                        </option>
                        <option
                            v-for="note in noteOptions"
                            :key="note.id"
                            :value="note.id"
                        >
                            {{ note.name }}
                        </option>
                    </select>
                </b-form-group>

                <b-form-group
                    label-for="medical-record"
                    label="Medical Records:"
                    label-cols="4"
                >
                    <select
                        id="medical-record"
                        v-model="selectedRecord"
                        class="form-control"
                        :disabled="isAwaitingInformation"
                    >
                        <option :value="CRECORDS.MEDICAL_CREATE.NO">
                            Do not create medical records
                        </option>
                        <option :value="CRECORDS.MEDICAL_CREATE.YES">
                            Create medical records
                        </option>
                    </select>
                </b-form-group>

                <b-form-group
                    v-if="details.patient_required == 1"
                    label-for="patient"
                    label="Patient:"
                    label-cols="4"
                >
                    <vet-select
                        id="patient"
                        v-model="selectedPatient"
                        :client-id="details.client_id"
                        :show-menu="false"
                        module="client-patient"
                        placeholder="Patient Name (required)"
                        label="name"
                        name="patient"
                        v-validate="'required'"
                        :class="{ 'border-danger': errors.has('patient') }"
                        :status="[1, 3]"
                        :disabled="isAwaitingInformation"
                    />
                </b-form-group>

                <b-form-group
                    label-for="invoice"
                    label="Invoice:"
                    label-cols="4"
                >
                    <select
                        id="invoice"
                        v-model="selectedInvoice"
                        class="form-control"
                        :disabled="isAwaitingInformation"
                    >
                        <option
                            v-for="inv in invoices"
                            :value="inv.id"
                            :key="inv.id"
                        >
                            {{ inv.name }} ({{ _carbonDateDisplay(inv.start) }})
                        </option>
                        <option disabled="disabled" v-if="invoices.length > 0">
                            ───────────────
                        </option>
                        <option :value="0">Create a new invoice</option>
                    </select>
                </b-form-group>

                <b-form-group
                    v-if="showProviderField"
                    label-for="provider"
                    label="Provider:"
                    label-cols="4"
                >
                    <v-select
                        id="provider"
                        name="provider"
                        :options="processedProviders"
                        v-model="selectedProvider"
                        placeholder="Provider (required)"
                        item-text="label"
                        :class="{
                            'border-danger':
                                errors.has('provider') || preventConvert
                        }"
                        v-validate="'required'"
                        :disabled="isAwaitingInformation"
                    />
                </b-form-group>
            </div>
            <template v-if="showRabiesField">
                <hr />
                <span class="font-weight-bold">Rabies Tag</span>

                <div
                    class="row pt-2"
                    v-for="item in rabiesEstimateItems"
                    :key="item.id"
                >
                    <div class="col-8 align-content-center">
                        <label class="form-check-label">
                            <span class="font-weight-bold">{{
                                item.patientName
                            }}</span>
                            {{ item.itemName }}</label
                        >
                    </div>
                    <div class="col-4">
                        <input
                            v-model="item.tag"
                            class="form-control"
                            placeholder="Tag (Optional)"
                        />
                    </div>
                </div>
            </template>
            <template v-slot:modal-footer>
                <loader v-if="isLoading || isAwaitingInformation" :margin="1" />

                <b-button
                    class="mr-auto"
                    variant="secondary"
                    @click="close"
                    v-if="!isLoading && !isAwaitingInformation"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="secondary"
                    @click="showNext = true"
                    v-if="
                        !isLoading &&
                        !isAwaitingInformation &&
                        selectActivity &&
                        !showNext
                    "
                >
                    Back
                </b-button>

                <!-- @click="showNext = false"  -->
                <b-button
                    v-if="
                        !isLoading &&
                        !isAwaitingInformation &&
                        details &&
                        isNewActivityWindow
                    "
                    variant="primary"
                    @click="next"
                    :disabled="preventConvert"
                >
                    Next
                </b-button>

                <b-button
                    v-else-if="
                        !isLoading &&
                        !isAwaitingInformation &&
                        details &&
                        !showNext
                    "
                    variant="primary"
                    @click="convert"
                    :disabled="preventConvert || isAwaitingInformation"
                >
                    Convert
                </b-button>
            </template>
        </b-modal>
        <multipleRequisitionForm ref="multipleRequisitionFormWindow" />
    </div>
</template>
<script type="text/javascript">
import {
    forEach,
    indexOf,
    find,
    every,
    isEqual,
    some,
    orderBy,
    cloneDeep,
    get,
    set,
    union,
    times,
    filter
} from "lodash";
import uuidv4 from "uuid/v4";
import Billings from "@/services/client-billings";
import Templates from "@/services/templates";
import Staff from "@/services/staff";
import CRECORDS from "@/constants/records";
import CBILLINGS from "@/constants/billings";
import ScheduleHelper from "@/module/schedule/helper"; // for _print_lab_order
import Loader from "@/module/commons/loader";
import MultipleRequisitionForm from "@/module/commons/print_multiple_requisition_form.vue";
import CLINICPROVIDER from "@/constants/clinic-provider";
import LabMixin from "@/mixins/lab";
import ITEMTYPES from "@/constants/item-types";
import { DateTime } from "luxon";
import ItemRows from "@/module/schedule/treatment-board/template-item";

// Constant for readability
const DUE_AT_SPECIFIC_TIME = 2;

export default {
    mixins: [ScheduleHelper, LabMixin],
    components: {
        Loader,
        ItemRows,
        MultipleRequisitionForm,
        vSelect: () => import("vue-select"),
        vetSelect: () => import("@/module/commons/vet-select.vue")
    },
    props: {
        modalRef: {
            type: String,
            required: true
        },
        estimate: {
            type: Object,
            default() {
                return { name: null };
            }
        },
        dashboard: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            CRECORDS,
            wellnessMsg:
                " If a patient is enrolled in a wellness plan, the terms of that plan will be applied to the items in this estimate.",
            actionMsg: " Actions will be applied to invoice. ",
            details: null,
            invoiceLoad: false,
            providerLoad: false,
            noteLoad: false,
            singleNote: null,
            multipleNotes: [],
            noteOptions: [],
            selectedRecord: 1,
            patients: [],
            invoices: [],
            providers: [],
            processedProviders: [],
            selectedInvoice: 0,
            selectedProvider: null,
            selectedPatient: null,
            selectedPrice: 0,
            notesHasError: false,
            isLoading: false,
            isDefaultProvider: false,
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT,
            providerMsg:
                " Some of items in this estimate are already assigned to an active provider. Please select a provider below for the rest of the items. ",
            declinedExists: false,
            rabiesEstimateItems: [],
            showNext: false,
            templates: [],
            estimateItemIds: [],
            groupedActivity: {},
            selectedAll: true,
            selectedQuantity: 0
        };
    },
    computed: {
        isOkDisabled() {
            return this.selectedProvider == null || this.selectedProvider == "";
        },
        showProviderField() {
            return Boolean(
                this.multipleNotes.length > 0 ||
                    this.singleNote ||
                    this.details.provider_required == 1
            );
        },
        preventConvert() {
            return Boolean(
                this.showProviderField &&
                    this.isDefaultProvider &&
                    this.details.containsLabRadiologyItem
            );
        },
        isNewActivityWindow() {
            return this.selectActivity && this.showNext;
        },
        showRabiesField() {
            return (
                this.rabiesEstimateItems.length > 0 &&
                !this.isNewActivityWindow &&
                this.selectedRecord === CRECORDS.MEDICAL_CREATE.YES
            );
        },
        isAwaitingInformation() {
            return this.noteLoad || this.invoiceLoad || this.providerLoad;
        },
        title() {
            let title = "";
            if (this.details) {
                if (this.selectActivity) {
                    if (this.showNext) {
                        title = "New Activity";
                    } else {
                        title = "Convert to Invoice";
                    }
                } else {
                    title = "Convert to Invoice";
                }
            }

            return title;
        },
        isShowSelectRange() {
            return this.templates.find(
                template => template?.estimateItem?.quantity_max != -1
            );
        },
        estimateActivities() {
            return this.details?.treatment_board_activity;
        },
        nonMedicalActivities() {
            return this.details?.treatment_board_non_medical_activity;
        },
        selectActivity() {
            return Boolean(this.estimateActivities);
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        }
    },
    watch: {
        selectedProvider: {
            handler(newData, oldData) {
                if (newData !== "") {
                    if (this.processedProviders.length === 0) {
                        this.selectedProvider = null;
                    } else {
                        this.setStaffId(this.providers);
                    }
                }
            }
        },
        selectedQuantity(val) {
            this.selectedPrice = val;
        },
        selectActivity(status) {
            this.showNext = status;
            if (status) {
                this.generateActivities();
            }
        }
    },
    methods: {
        open() {
            this.$refs[this.modalRef].show();

            this.$nextTick(() => {
                this.getEstimateDetails();
            });
        },
        getEstimateDetails() {
            Billings.getEstimate(this.estimate.id)
                .then(response => {
                    this.details = response.resource;
                })
                .then(() => {
                    this.getNoteOptions();
                    this.getInvoices();
                    this.getProviders();
                    this.checkDeclinedItems();

                    if (!this.selectActivity) {
                        this.getRabiesItems();
                    }
                });
        },
        checkDeclinedItems() {
            let self = this;
            const allItemsAreActive = every(self.details.estimate_items, item =>
                some(self.details.active_estimate_items, activeItem =>
                    isEqual(item.id, activeItem.id)
                )
            );
            this.declinedExists = !allItemsAreActive;
        },
        getRabiesItems() {
            let self = this;
            self.rabiesEstimateItems = [];
            // Filter the active estimate items to get the estimate items that will not create as activity
            const estimateItems = filter(
                self.details.active_estimate_items,
                activeEstimateItems => {
                    return !self.estimateItemIds.includes(
                        activeEstimateItems.id
                    );
                }
            );

            estimateItems.forEach(activeEstimateItem => {
                const estimateItem = find(self.details.estimate_items, {
                    id: activeEstimateItem.id
                });
                const item = estimateItem.item;
                if (item && self.checkElementIsRabies(item)) {
                    self.rabiesEstimateItems.push({
                        patientName: activeEstimateItem.patient_name,
                        itemName: item.name,
                        id: activeEstimateItem.id,
                        tag: ""
                    });
                }
            });
        },
        checkElementIsRabies(item) {
            if (item.type != ITEMTYPES.TYPES.TYPE_LAB) {
                const lowerCaseName = item.name.toLowerCase();
                return lowerCaseName.includes("rabies");
            }
        },
        getProviders() {
            this.providerLoad = true;
            Staff.getProviders()
                .then(response => {
                    this.providers = response.resource.data;
                })
                .then(() => {
                    this.setProvider();
                })
                .finally(() => {
                    this.providerLoad = false;
                });
        },
        setProvider() {
            let list = [];

            this.providers.forEach(e => {
                list.push(e.actor);
            });

            this.processedProviders = list;
            if (list.length == 0) {
                this.selectedProvider = null;
            } else {
                this.setStaffId(this.providers);
            }
        },
        setStaffId(providerOptions) {
            let self = this;
            self.isDefaultProvider = false;
            let currentStaff = find(providerOptions, function (opt) {
                if (self.selectedProvider) {
                    let id =
                        "actor_id" in self.selectedProvider
                            ? self.selectedProvider.actor_id
                            : self.selectedProvider.id;
                    return opt.actor_id == id;
                }
            });
            let defaultClinicProvider = find(providerOptions, [
                "is_default_provider",
                true
            ]);

            if (!currentStaff) {
                let providerStaff = find(providerOptions, [
                    "actor_id",
                    this.$root.currentActor.id
                ]);
                if (providerStaff) {
                    self.selectedProvider = providerStaff.actor;
                } else if (defaultClinicProvider) {
                    // default the provider if cannot find provider
                    self.isDefaultProvider = true;
                    self.selectedProvider = defaultClinicProvider.actor;
                }
            } else {
                if (defaultClinicProvider) {
                    self.isDefaultProvider =
                        self.selectedProvider.id ==
                        defaultClinicProvider.actor.id;
                }
            }
        },
        getInvoices() {
            this.invoiceLoad = true;
            let params = {
                type: CBILLINGS.BILLING_TYPE.OPEN_INVOICES
            };

            Billings.getBillings(this.details.client_id, params)
                .then(response => {
                    this.invoices = response.resources;
                })
                .then(() => {
                    if (this.invoices.length > 0)
                        this.selectedInvoice = this.invoices[0].id;
                })
                .finally(() => {
                    this.invoiceLoad = false;
                });
        },
        setNoteOptions() {
            let self = this;
            if (self.details.active_soap.length > 0) {
                forEach(self.noteOptions, function (note) {
                    forEach(self.details.active_soap, function (soap) {
                        if (note.id === soap.id) {
                            self.multipleNotes.push(note);
                        }
                    });
                });
            }
        },
        getNoteOptions() {
            this.noteLoad = true;
            this.noteOptions = [];

            if (this.details.active_soap.length > 0) {
                Billings.getEstimateSoap(this.estimate.id)
                    .then(response => {
                        this.noteOptions = response.resource;
                    })
                    .then(() => {
                        this.setNoteOptions();
                    })
                    .finally(() => {
                        this.noteLoad = false;
                    });
            } else {
                Templates.getSoap()
                    .then(response => {
                        this.noteOptions = response.resources;
                    })
                    .finally(() => {
                        this.noteLoad = false;
                    });
            }
        },
        isWellnessPlanEnabled() {
            let self = this;
            let status = false;
            forEach(self.$root._data.extensions, function (data) {
                if (
                    data.extension_code == "WELLNESS" &&
                    data.status == "ENABLED"
                ) {
                    status = true;
                }
            });
            return status;
        },
        onNotesChange() {
            let self = this;
            let notes = this.multipleNotes;
            let patients = [];
            self.notesHasError = false;

            if (this.multipleNotes.length > 1) {
                forEach(notes, function (note) {
                    if (indexOf(patients, note.patient_id) == -1) {
                        patients.push(note.patient_id);
                    } else {
                        self.notesHasError = true;
                    }
                });
            }
        },
        async next() {
            this.groupedActivity = {};

            let selectedActivities = this.templates.filter(
                template => template.selected
            );
            let estimateItemIds = selectedActivities.map(
                template => template.estimateItemId
            );
            this.estimateItemIds = [...new Set(estimateItemIds)].filter(
                id => id != null
            );

            this.getRabiesItems();

            for (const activity of selectedActivities) {
                const boardId = activity.treatment_board_id;
                const templateId = activity.templateId;

                // Ensure treatment board group exists
                if (!this.groupedActivity[boardId]) {
                    this.groupedActivity[boardId] = {
                        activities: [],
                        patient: activity.patient,
                        soapId: activity.soapId,
                        templateId,
                        treatmentBoardId: boardId
                    };
                }

                // Push activity into the group
                this.groupedActivity[boardId].activities.push(activity);
            }
            this.showNext = false;
        },
        async convert() {
            let valid = await this.$validator.validateAll();
            if (!valid || (this.isOkDisabled && this.showProviderField)) {
                return false;
            }

            this.isLoading = true;

            let params = {
                client_id: this.details.client_id,
                estimate_id: this.estimate.id,
                invoice_id: this.selectedInvoice ? this.selectedInvoice : null,
                patient_id: this.selectedPatient ? this.selectedPatient.id : "",
                provider_id: this.showProviderField
                    ? this.selectedProvider.id
                    : null,
                record_option: this.selectedRecord,
                action_count: this.details.action_count,
                range: this.selectedPrice,
                rabies_estimate_items: this.rabiesEstimateItems.map(item => ({
                    id: item.id,
                    tag: item.tag || null
                }))
            };

            if (this.selectActivity) {
                params.estimate_item_ids = this.estimateItemIds;

                for (const treatment of Object.values(this.groupedActivity)) {
                    await this.saveActivity(treatment);
                }
            }

            if (this.details.active_soap.length > 0) {
                params.soap_options = [];

                forEach(this.multipleNotes, function (note) {
                    params.soap_options.push(note.id + "#$" + note.patient_id);
                });
            } else {
                params.soap_option = this.singleNote ? this.singleNote : "";
            }

            try {
                let self = this;
                let response = await Billings.convertEstimate(params);
                let responseResource = response.resources;

                // Prevent from redirect when the API convert return error like "Fatal Error"
                if (
                    typeof response == "string" &&
                    response.toLowerCase().includes("fatal error")
                ) {
                    throw "Error";
                }

                window.localStorage.setItem(
                    "convertedEstimate",
                    response.message
                );

                if (response.action_response) {
                    self.loading = false;
                    var showSuccess = true;
                    var showError = true;

                    for (let i = 0; i < response.action_response.length; i++) {
                        $.each(response.action_response[i], function (
                            key,
                            value
                        ) {
                            if (key == "uri") {
                                window.open(value);
                            }
                            if (key == "success" && showSuccess == true) {
                                self._message(value);
                            }
                            if (key == "error" && showError == true) {
                                self._message(value, "danger");
                            }
                        });
                    }
                }

                let lab = responseResource.lab;
                let isAntechItem = lab.antech_token && lab.accession_id; // antech v6

                if (isAntechItem) {
                    let labResponse = {};

                    if (
                        (lab.lab_ids && lab.lab_ids.length > 0) ||
                        (lab.idexx_lab_id && lab.idexx_lab_id.length > 0)
                    ) {
                        var source = lab.lab_source;

                        if (
                            lab.lab_source.length == 1 &&
                            lab.lab_source.includes("IDEXX_DEVICE")
                        ) {
                            source = "IDEXX_DEVICE";
                        }

                        labResponse = await this.getLabIntegrationObject(
                            lab,
                            "invoice_dashboard",
                            {
                                module_url:
                                    "billing/invoice/view/" +
                                    response.resources.invoice_encoded_id,
                                isEstimate: true,
                                source: source
                            }
                        );
                    }

                    self.$root.$emit("call-antech-iframe", {
                        antech_token: lab.antech_token,
                        accession_id: lab.accession_id,
                        lab_response: labResponse
                    });

                    this.$router.push({
                        name: "invoice",
                        params: {
                            id: responseResource.invoice_encoded_id
                        },
                        query: {
                            status: responseResource.reponse
                        }
                    });
                } else if (
                    (responseResource.lab.lab_ids &&
                        responseResource.lab.lab_ids.length > 0) ||
                    (responseResource.lab.idexx_lab_id &&
                        responseResource.lab.idexx_lab_id.length > 0)
                ) {
                    let printLabParams = {
                        idexx_lab_id: lab.idexx_lab_id,
                        idexx_url: lab.idexx_url,
                        lab_ids: lab.lab_ids,
                        source: lab.lab_source,
                        module: "invoice_dashboard",
                        module_url:
                            "billing/invoice/view/" +
                            response.resources.invoice_encoded_id,
                        isEstimate: true
                    };

                    if (
                        lab.lab_source.length == 1 &&
                        lab.lab_source.includes("IDEXX_DEVICE")
                    ) {
                        printLabParams.source = "IDEXX_DEVICE";
                    }

                    self._print_lab_order(printLabParams).then(function (
                        labResponse
                    ) {
                        if (labResponse.idexx_iframe) {
                            //load idexx iframe

                            labResponse.module_url = printLabParams.module_url;
                            labResponse.module = printLabParams.module;

                            self.$root.$emit("call_idexx_iframe", labResponse);
                        } else {
                            //load requisition form modal

                            labResponse.module_url = printLabParams.module_url;
                            labResponse.module = printLabParams.module;

                            self.$root.$emit(
                                "call-multirequsition-modal",
                                labResponse
                            );
                        }
                    });

                    this.isLoading = false;
                    this.close();
                } else if (
                    typeof response.idexx_error_msg !== "undefined" &&
                    response.idexx_error_msg != null &&
                    response.idexx_error_msg != ""
                ) {
                    this._message(response.idexx_error_msg, "danger");
                    this.isLoading = false;
                } else {
                    // status to show the message on estimate page in CI
                    if (response.discount_change) {
                        this.$router.push({
                            name: "invoice",
                            params: {
                                id: response.resources.invoice_encoded_id
                            },
                            query: {
                                status: response.resources.reponse,
                                dc: 1
                            }
                        });
                    } else {
                        this.$router.push({
                            name: "invoice",
                            params: {
                                id: response.resources.invoice_encoded_id
                            },
                            query: {
                                status: response.resources.reponse
                            }
                        });
                    }
                }
            } catch (error) {
                this.isLoading = false;
                this._message("Error : Unable to convert estimate", "danger");
            }
        },
        close() {
            /**
             * no need to set `isLoading=false` here.
             * this is to prevent user clicking multiple times
             * right before the modal is closed.
             * this modal is `lazy=true` means each time it's being opened
             * the states will reset and `isLoading` is automatically set to `false`.
             */
            this.$refs[this.modalRef].hide();
        },
        generateActivities() {
            let templates = [];
            let labelOptions = this.labelOptions;

            for (const [key, value] of Object.entries(
                this.estimateActivities
            )) {
                let activities = value.activities;
                let patient = value.patient;
                let estimateItem = value.estimate_item;
                let treatmentBoard = value.treatment_board;

                activities.forEach(activity => {
                    activity.estimateItem = estimateItem;
                    activity.treatmentBoard = treatmentBoard;
                    activity.patient = patient;
                    activity.estimateItemId = key;
                    templates.push(activity);
                });
            }

            for (const [key, value] of Object.entries(
                this.nonMedicalActivities
            )) {
                let activities = value.non_medical_template_activities;
                let patient = value.patient;
                let treatmentBoard = value.treatment_board;

                activities.forEach(activity => {
                    activity.treatmentBoard = treatmentBoard;
                    activity.patient = patient;
                    activity.newId = uuidv4();
                    templates.push(activity);
                });
            }

            let occurenceArray = [];
            templates.forEach(template => {
                template.id = template.estimateItemId || template.newId;
                if (get(template, "activity.repeat.occurence") > 1) {
                    template.id = uuidv4();
                    template.occurrence_id = template.id;
                    times(get(template, "activity.repeat.occurence"), time => {
                        if (time < 1) {
                            return;
                        }
                        let interval = get(
                            template,
                            "activity.repeat.specific_interval"
                        );
                        let object = cloneDeep(template);

                        // -- assign temporary uniqid
                        object.id = uuidv4();
                        if (template.activity.due.type === 1) {
                            set(
                                object,
                                "activity.due.after_start_time",
                                get(template, "activity.due.after_start_time") +
                                    interval * time
                            );
                        } else {
                            set(
                                object,
                                "activity.due.specific_time",
                                DateTime.fromFormat(
                                    get(template, "activity.due.specific_time"),
                                    "hh:mm a"
                                )
                                    .plus({
                                        minutes: interval * time
                                    })
                                    .toSeconds()
                            );
                        }
                        occurenceArray.push(object);
                    });
                }
            });
            let currentTime = this._now();
            this.templates = orderBy(
                this.addOneDay(
                    union(templates, occurenceArray).map(template => ({
                        id: template.id,
                        estimateItemId: template.estimateItemId,
                        patient: template.patient,
                        occurrence_id: template.occurrence_id,
                        selected: true,
                        treatment_board_id: template.treatmentBoard.id,
                        treatment_activity_type_id: template.activity_type,
                        repeat_data: template.activity.repeat,
                        due: this._calculateDue(
                            currentTime,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            currentTime
                        ).diff,
                        due_type: template.activity.due.type,
                        time: this._calculateDue(
                            currentTime,
                            template.activity.due.type === 1
                                ? template.activity.due.after_start_time
                                : template.activity.due.specific_time,
                            template.activity.due.type,
                            currentTime
                        ).time,
                        note: "",
                        start: template.treatmentBoard.start,
                        soapId: template.treatmentBoard.soap_id,
                        templateId: template.template_id,
                        estimateItem: template.estimateItem,
                        medications: template.activity.item_id
                            ? [
                                  {
                                      item_id: template.estimateItem.item.id,
                                      name: template.estimateItem.item.name,
                                      uom: template.estimateItem.item.uom,
                                      status: 0,
                                      quantity: template.activity.quantity,
                                      route_id: template.activity.route_id,
                                      location_id:
                                          template.activity.location_id,
                                      template_element_id: template.template_id
                                  }
                              ]
                            : undefined,
                        otherTasks: [
                            {
                                label_id: get(template, "activity.label_id", 1),
                                taskType:
                                    template.activity_type === 3
                                        ? find(labelOptions, {
                                              value: parseInt(
                                                  template.activity.label_id
                                              )
                                          }).text
                                        : null,
                                task: template.activity.task,
                                status: 0
                            }
                        ],
                        vitals: template.master_vital
                            ? template.master_vital.map(item => {
                                  let imperial = this._setting.weights_measures;
                                  let masterUom = [];
                                  switch (parseInt(item.id)) {
                                      case 3:
                                          masterUom = find(item.master_uom, {
                                              uom: "F"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "C"
                                                  }
                                              );
                                          break;
                                      case 4:
                                          masterUom = find(item.master_uom, {
                                              uom: "Lbs"
                                          });
                                          if (imperial === 2)
                                              masterUom = find(
                                                  item.master_uom,
                                                  {
                                                      uom: "Kg"
                                                  }
                                              );
                                          break;
                                      default:
                                          masterUom = item.master_uom[0];
                                          break;
                                  }

                                  return {
                                      master_vital_id: item.id,
                                      name: item.name,
                                      value: null,
                                      patient_id: template.patient,
                                      master_uom_id: masterUom
                                          ? masterUom.id
                                          : 0,
                                      status: 0
                                  };
                              })
                            : undefined
                    }))
                ),
                "time",
                "asc"
            );
        },
        /**
         * Ensure every activity’s `due` / `time` is not before `now`.
         * If the first occurrence in a chain moves, its children move too.
         * Mutates the objects in-place and also returns the array for chaining.
         */
        addOneDay(activities) {
            const tz = this._timeZone; // grab once
            const now = this._now(); // Luxon DateTime in the same zone
            const bumpedSet = new Set(); // parents that were shifted

            // helper: return epoch, shifted +1 day if in the past
            const shiftIfPast = epoch => {
                const dt = DateTime.fromSeconds(epoch).setZone(tz);
                return dt < now ? dt.plus({ days: 1 }).toSeconds() : epoch;
            };

            return activities.map(act => {
                const isParent = act.id === act.occurrence_id;
                const parentBump = bumpedSet.has(act.occurrence_id);

                //------------------------------------------
                // Case A – This is the parent in the chain
                //------------------------------------------
                if (act.occurrence_id && isParent) {
                    const oldDue = act.due;
                    const oldTime = act.time;

                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = shiftIfPast(act.due);
                    }
                    act.time = shiftIfPast(act.time);

                    // Record that this chain must be shifted
                    if (oldDue !== act.due || oldTime !== act.time) {
                        bumpedSet.add(act.occurrence_id);
                    }
                    return act;
                }

                //------------------------------------------------------
                // Case B – Child occurrence whose parent was bumped
                //------------------------------------------------------
                if (act.occurrence_id && parentBump) {
                    if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                        act.due = DateTime.fromSeconds(act.due)
                            .setZone(tz)
                            .plus({ days: 1 })
                            .toSeconds();
                    }
                    act.time = DateTime.fromSeconds(act.time)
                        .setZone(tz)
                        .plus({ days: 1 })
                        .toSeconds();
                    return act;
                }

                //------------------------------------------
                // Case C – Not recurring OR parent untouched
                //------------------------------------------
                if (act.due_type === DUE_AT_SPECIFIC_TIME) {
                    act.due = shiftIfPast(act.due);
                }
                act.time = shiftIfPast(act.time);
                return act;
            });
        },
        async saveActivity(treatment) {
            let self = this;
            self.loading = true;
            let valid = await self.$validator.validateAll();

            if (valid) {
                let params = {
                    patient_id: treatment.patient.id,
                    client_id: self.details.client_id,
                    template_id: null,
                    status_id: null,
                    invoice_id: null,
                    soap_id:
                        treatment.soapId !== -1 && treatment.soapId
                            ? treatment.soapId
                            : null,
                    activities: treatment.activities,
                    check_treatment_status: true
                };

                try {
                    if (params) {
                        await this.$http
                            .put(
                                process.env.BARRAMUNDI +
                                    "schedule/treatment-board/" +
                                    treatment.treatmentBoardId,
                                params
                            )
                            .then(async response => {
                                if (response.data?.response?.code === 403) {
                                    self._message(
                                        response.data?.response?.message,
                                        "warning"
                                    );
                                } else if (response.status === 200) {
                                    self.$socket.client.emit("v-broadcast", {
                                        action: "refresh-treatment",
                                        businessId: this._setting.business_id
                                    });

                                    self._message(
                                        "Activity created",
                                        "success"
                                    );
                                } else {
                                    self.loading = false;
                                    self._message(
                                        `Unable to create activities for patient ${treatment.patient.name} due to an unidentified error.`,
                                        "warning"
                                    );
                                }
                            });
                    }
                } catch (errors) {
                    console.log(errors, params);
                    self.loading = false;
                    self._message(
                        `Unable to create activities for patient ${treatment.patient.name} due to an unidentified error.`,
                        "warning"
                    );
                }
            }
        },
        selectAll() {
            this.templates = this.templates.map(template => {
                if (some(template.medications)) {
                    if (!this.selectedAll) {
                        if (template.originalQuantity) {
                            template.medications[0].quantity =
                                template.originalQuantity;
                        }
                    } else {
                        template.originalQuantity =
                            template.medications[0].quantity;
                        template.medications[0].quantity = 0;
                    }
                }
                return {
                    ...template,
                    selected: !this.selectedAll
                };
            });
        },
        updateSelectedAll() {
            this.selectedAll = !this.templates.find(
                template => !template.selected
            );
        }
    }
};
</script>
